import React, { useState } from 'react';
import { uploadFile, compressImageForUpload } from '../firebase';
import { getSocket, isSocketConnected } from '../socket';
import { DEFAULT_AVATAR } from '../utils/defaultAvatar';
import './GroupSettings.css';

function GroupSettings({ room, user, onClose }) {
  const [groupName, setGroupName] = useState(room.name || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewURL, setPreviewURL] = useState(room.photoURL || null);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be smaller than 5MB');
        return;
      }

      setPhotoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewURL(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleDeleteGroup = () => {
    if (!room || !room.roomId) return;
    if (!window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) return;
    try {
      const socket = getSocket();
      if (socket) {
        socket.emit('room:delete:group', { roomId: room.roomId });
        onClose();
      } else {
        setError('Not connected to server');
      }
    } catch (e) {
      console.error('Error deleting group:', e);
      setError('Failed to delete group. Please try again.');
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPreviewURL(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let newPhotoURL = room.photoURL;

      // Upload new photo if selected
      if (photoFile) {
        const compressed = await compressImageForUpload(photoFile, { maxDimension: 768, quality: 0.82 });
        const path = `group-pictures/${room.roomId}/${Date.now()}_${compressed.name}`;
        newPhotoURL = await uploadFile(compressed, path, {
          cacheControl: 'public, max-age=604800',
          contentType: compressed.type
        });
      } else if (previewURL === null && room.photoURL) {
        // User removed the photo
        newPhotoURL = null;
      }

      // Emit update to server
      const socket = getSocket();
      if (socket) {
        socket.emit('room:update:group', {
          roomId: room.roomId,
          name: groupName.trim(),
          photoURL: newPhotoURL
        });

        setSuccess('Group updated successfully!');
        setPhotoFile(null);
        
        // Close after a short delay
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        setError('Not connected to server');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      setError('Failed to update group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="group-settings-overlay" onClick={onClose}>
      <div className="group-settings-container" onClick={(e) => e.stopPropagation()}>
        <div className="group-settings-header">
          <h2>Group Settings</h2>
          <button onClick={onClose} className="close-btn" title="Close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="group-settings-form">
          <div className="photo-section">
            <div className="photo-preview">
              {previewURL ? (
                <img src={previewURL} alt="Group" className="group-photo" />
              ) : (
                <div className="no-photo">
                  <span className="material-symbols-outlined">group</span>
                </div>
              )}
            </div>
            <div className="photo-actions">
              <label htmlFor="group-photo-input" className="upload-photo-btn">
                <span className="material-symbols-outlined">photo_camera</span>
                Change Photo
              </label>
              <input
                id="group-photo-input"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                disabled={loading}
                style={{ display: 'none' }}
              />
              {previewURL && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="remove-photo-btn"
                  disabled={loading}
                >
                  <span className="material-symbols-outlined">delete</span>
                  Remove Photo
                </button>
              )}
            </div>
            <p className="photo-hint">JPG, PNG or GIF. Max size 5MB.</p>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label htmlFor="groupName">Group Name</label>
              <input
                id="groupName"
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name"
                className="group-settings-input"
                disabled={loading}
                maxLength={50}
                required
              />
            </div>
          </div>

          {error && <div className="group-settings-error">{error}</div>}
          {success && <div className="group-settings-success">{success}</div>}

          <div className="group-settings-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={loading || !groupName.trim()}
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>

        <div className="form-section">
          <div className="form-group">
            <label>Archive</label>
            <button
              type="button"
              onClick={() => {
                const socket = getSocket();
                if (socket && isSocketConnected()) {
                  socket.emit('room:archive', { roomId: room.roomId, archived: true });
                }
                onClose();
              }}
              className="cancel-btn"
              disabled={loading}
              title="Hide this chat from the list; reopen it from Account Settings"
            >
              <span className="material-symbols-outlined">archive</span>
              Archive Group
            </button>
          </div>
        </div>

        <div className="form-section">
          <div className="form-group">
            <label>Danger Zone</label>
            <button
              type="button"
              onClick={handleDeleteGroup}
              className="delete-group-btn"
              disabled={loading}
              title="Delete this group"
            >
              <span className="material-symbols-outlined">delete_forever</span>
              Delete Group
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GroupSettings;

